import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Container, Grid } from '@mui/material';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import EmojiNatureIcon from '@mui/icons-material/EmojiNature';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';

const differentiators = [
  {
    icon: <BarChartRoundedIcon sx={{ fontSize: 48, mb: 1, color: '#fff' }} />,
    title: 'Analytics-Driven',
    subtext: 'Insightful reports',
  },
  {
    icon: <GroupsRoundedIcon sx={{ fontSize: 48, mb: 1, color: '#fff' }} />,
    title: 'Dedicated Team',
    subtext: 'Global support',
  },
  {
    icon: <EmojiNatureIcon sx={{ fontSize: 48, mb: 1, color: '#fff' }} />,
    title: 'FSC Certified',
    subtext: 'Environment-first',
  },
  {
    icon: <Inventory2RoundedIcon sx={{ fontSize: 48, mb: 1, color: '#fff' }} />,
    title: 'Stockless Efficiency',
    subtext: 'Trade without warehousing',
  },
];

const WhyChooseUs = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const fadeInStyle = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'none' : 'translateY(40px)',
    transition: 'opacity 1.1s cubic-bezier(.23,1.01,.32,1), transform 1.1s cubic-bezier(.23,1.01,.32,1)',
  };

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: '100vw',
        aspectRatio: { xs: 'unset', sm: '16 / 9' },
        minHeight: { xs: '100vh', sm: 'auto' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        overflowX: 'hidden',
        my: { xs: 0, md: 0 },
        px: { xs: 0.5, sm: 0 }, // Add horizontal padding for mobile
      }}
    >
      {/* Background Image as <img> */}
      <Box
        component="img"
        src="/banner/banner4.jpg"
        alt="Why Choose Us Background"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          zIndex: 1,
          filter: 'blur(1px)',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />
      {/* Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          bgcolor: 'rgba(0,0,0,0.55)',
          zIndex: 2,
        }}
      />
      {/* Centered Content */}
      <Container
        sx={{
          position: 'relative',
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: { xs: '100vh', sm: 'auto' },
          ...fadeInStyle,
          px: { xs: 0.5, sm: 0 },
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: 900,
            fontSize: { xs: '1.4rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
            mb: { xs: 1, sm: 1 },
            color: '#fff',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            textShadow: '0 4px 24px rgba(0,0,0,0.7), 0 1px 2px rgba(0,0,0,0.7)',
            textAlign: 'center',
          }}
        >
          Why Choose Us
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            color: '#f5f3e7',
            fontWeight: 400,
            fontSize: { xs: '1rem', md: '1.25rem' },
            mb: { xs: 2, sm: 4 },
            textAlign: 'center',
            textShadow: '0 2px 8px rgba(0,0,0,0.7)',
          }}
        >
          What makes Digi Wood different in the global timber trade
        </Typography>
        <Box
          sx={{
            bgcolor: 'rgba(30,30,30,0.55)',
            borderRadius: 4,
            boxShadow: '0 8px 32px 0 rgba(0,0,0,0.25)',
            p: { xs: 1, md: 4 },
            maxWidth: 900,
            width: '100%',
            mx: 'auto',
            mb: 2,
          }}
        >
          <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center">
            {differentiators.map((item, idx) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                key={item.title}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  py: { xs: 2, sm: 3 },
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.04)',
                    boxShadow: '0 4px 24px 0 rgba(255,255,255,0.10)',
                  },
                  minWidth: 0,
                }}
              >
                {item.icon}
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 800, mb: 1, letterSpacing: '0.04em', fontSize: { xs: '1rem', sm: '1.18rem' } }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#f5f3e7', fontWeight: 400, fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                  {item.subtext}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default WhyChooseUs; 